// src/app/shared/notifications/notifications.component.ts

import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, combineLatest, filter, map, startWith, takeUntil } from 'rxjs';

import { RequiredNotificationsService } from '../notifications.service';
import {
  BookedHourNotificationDto,
  DayOffRequestGroupNotificationDto,
  UserNotificationsDto,
} from '../notifications.model';

interface BookedHourChangeModalVm {
  row: BookedHourNotificationDto;
  isSelfApproval: boolean;
  newBookedHours: number;
  bypassEmployeeApproval: boolean;
  errorMessage: string | null;
}

type ConfirmationAction =
  | { type: 'approveDayOff'; group: DayOffRequestGroupNotificationDto }
  | { type: 'rejectDayOff'; group: DayOffRequestGroupNotificationDto }
  | { type: 'approveSupervisorBookedHour'; row: BookedHourNotificationDto }
  | { type: 'rejectSupervisorBookedHour'; row: BookedHourNotificationDto }
  | { type: 'approveSelfBookedHour'; row: BookedHourNotificationDto }
  | { type: 'rejectSelfBookedHour'; row: BookedHourNotificationDto }
  | { type: 'submitBookedHourChange' }
  | { type: 'approveAll' };

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [CommonModule, FormsModule, DatePipe, DecimalPipe],
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly dismissedOnHoursSubject = new BehaviorSubject<boolean>(
    sessionStorage.getItem('hoursNotificationsDismissed') === 'true',
  );

  readonly notifications$: Observable<UserNotificationsDto>;
  readonly isLoading$: Observable<boolean>;
  readonly isActionRunning$: Observable<boolean>;
  readonly hasBlockingNotifications$: Observable<boolean>;
  readonly errorMessage$: Observable<string | null>;
  readonly currentFullYearWeek$: Observable<string | null>;

  readonly shouldShowModal$: Observable<boolean>;

  bookedHourChangeModal: BookedHourChangeModalVm | null = null;
  confirmationAction: ConfirmationAction | null = null;

  constructor(
    private readonly requiredNotificationsService: RequiredNotificationsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly router: Router,
  ) {
    this.notifications$ = this.requiredNotificationsService.notifications$;
    this.isLoading$ = this.requiredNotificationsService.isLoading$;
    this.isActionRunning$ = this.requiredNotificationsService.isActionRunning$;
    this.hasBlockingNotifications$ = this.requiredNotificationsService.hasBlockingNotifications$;
    this.errorMessage$ = this.requiredNotificationsService.errorMessage$;
    this.currentFullYearWeek$ = this.requiredNotificationsService.currentFullYearWeek$;

    const isHoursRoute$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => this.isHoursUrl(event.urlAfterRedirects)),
      startWith(this.isHoursUrl(this.router.url)),
    );

    this.shouldShowModal$ = combineLatest([
      this.hasBlockingNotifications$,
      isHoursRoute$,
      this.dismissedOnHoursSubject.asObservable(),
    ]).pipe(
      map(([hasNotifications, isHoursRoute, dismissed]) => {
        return hasNotifications && isHoursRoute && !dismissed;
      }),
    );

    isHoursRoute$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isHoursRoute) => {
        if (!isHoursRoute) {
          sessionStorage.removeItem('hoursNotificationsDismissed');
          this.dismissedOnHoursSubject.next(false);
        }
      });
  }

  ngOnInit(): void {
    this.requiredNotificationsService.loadRequiredNotifications();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeModal(): void {
    sessionStorage.setItem('hoursNotificationsDismissed', 'true');
    this.dismissedOnHoursSubject.next(true);
    this.closeBookedHourChangeModal();
    this.cancelConfirmation();
    this.cdr.markForCheck();
  }

  refresh(): void {
    sessionStorage.removeItem('hoursNotificationsDismissed');
    this.dismissedOnHoursSubject.next(false);
    this.requiredNotificationsService.loadRequiredNotifications();
  }

  approveAll(): void {
    this.confirmationAction = { type: 'approveAll' };
    this.cdr.markForCheck();
  }

  getTotalCount(notifications: UserNotificationsDto | null | undefined): number {
    return this.requiredNotificationsService.getTotalNotificationCount(notifications);
  }

  getPendingActionCount(notifications: UserNotificationsDto | null | undefined): number {
    return this.requiredNotificationsService.getPendingActionCount(notifications);
  }

  getEmployeeName(employee: { firstName?: string | null; lastName?: string | null } | null | undefined): string {
    if (!employee) {
      return '—';
    }

    return `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() || '—';
  }

  getDayOffTotalHours(group: DayOffRequestGroupNotificationDto): number {
    return (group.dayOffRequestDates ?? []).reduce(
      (total: number, day) => total + Number(day.totalHours ?? 0),
      0,
    );
  }

  getDayOffDatesLabel(group: DayOffRequestGroupNotificationDto): string {
    const dates = (group.dayOffRequestDates ?? [])
      .map((day) => day.date)
      .filter((date): date is string => !!date);

    return dates.length ? dates.join(', ') : '—';
  }

  isDayOffActionable(group: DayOffRequestGroupNotificationDto): boolean {
    return group.isApproved === null;
  }

  getBookedHourCompany(row: BookedHourNotificationDto): string {
    return row.company?.name ?? row.employee?.company?.name ?? '—';
  }

  getBookedHourDepartment(row: BookedHourNotificationDto): string {
    const department = row.department ?? row.employee?.department ?? null;

    if (!department) {
      return '—';
    }

    return `${department.code} - ${department.name}`;
  }

  getBookedHourWorkerType(row: BookedHourNotificationDto): string {
    return row.workerType?.name ?? '—';
  }

  getBookedHourYyww(row: BookedHourNotificationDto): string {
    return row.yyww?.fullYearWeek ?? row.yyww?.name ?? '—';
  }

  getBookedHourDetails(row: BookedHourNotificationDto): string {
    const parts = [
      this.getBookedHourCompany(row),
      this.getBookedHourDepartment(row),
      this.getBookedHourWorkerType(row),
    ].filter((value) => value && value !== '—');

    return parts.length ? parts.join(' • ') : '—';
  }

  approveDayOff(group: DayOffRequestGroupNotificationDto): void {
    this.confirmationAction = { type: 'approveDayOff', group };
    this.cdr.markForCheck();
  }

  rejectDayOff(group: DayOffRequestGroupNotificationDto): void {
    this.confirmationAction = { type: 'rejectDayOff', group };
    this.cdr.markForCheck();
  }

  approveSupervisorBookedHour(row: BookedHourNotificationDto): void {
    this.confirmationAction = { type: 'approveSupervisorBookedHour', row };
    this.cdr.markForCheck();
  }

  rejectSupervisorBookedHour(row: BookedHourNotificationDto): void {
    this.confirmationAction = { type: 'rejectSupervisorBookedHour', row };
    this.cdr.markForCheck();
  }

  openSupervisorBookedHourChange(row: BookedHourNotificationDto): void {
    this.openBookedHourChange(row, false);
  }

  openSelfBookedHourChange(row: BookedHourNotificationDto): void {
    this.openBookedHourChange(row, true);
  }

  approveSelfBookedHour(row: BookedHourNotificationDto): void {
    this.confirmationAction = { type: 'approveSelfBookedHour', row };
    this.cdr.markForCheck();
  }

  rejectSelfBookedHour(row: BookedHourNotificationDto): void {
    this.confirmationAction = { type: 'rejectSelfBookedHour', row };
    this.cdr.markForCheck();
  }

  approveBookedHour(row: BookedHourNotificationDto): void {
    this.approveSupervisorBookedHour(row);
  }

  rejectBookedHour(row: BookedHourNotificationDto): void {
    this.rejectSupervisorBookedHour(row);
  }

  closeBookedHourChangeModal(): void {
    this.bookedHourChangeModal = null;
    this.cdr.markForCheck();
  }

  onChangeHoursInput(value: number | string): void {
    if (!this.bookedHourChangeModal) {
      return;
    }

    const next = Number(value);

    this.bookedHourChangeModal = {
      ...this.bookedHourChangeModal,
      newBookedHours: Number.isFinite(next) ? Math.max(0, next) : 0,
      errorMessage: null,
    };

    this.cdr.markForCheck();
  }

  onBypassEmployeeApprovalChange(value: boolean): void {
    if (!this.bookedHourChangeModal) {
      return;
    }

    this.bookedHourChangeModal = {
      ...this.bookedHourChangeModal,
      bypassEmployeeApproval: !!value,
    };

    this.cdr.markForCheck();
  }

  submitBookedHourChange(): void {
    if (!this.bookedHourChangeModal) {
      return;
    }

    const modal = this.bookedHourChangeModal;
    const cleanHours = this.round2(Number(modal.newBookedHours));

    if (!Number.isFinite(cleanHours) || cleanHours < 0) {
      this.setBookedHourChangeError('Hours must be zero or greater.');
      return;
    }

    if (this.isMatch(cleanHours, Number(modal.row.hours) || 0)) {
      this.setBookedHourChangeError('Enter a different hour value before saving.');
      return;
    }

    this.confirmationAction = { type: 'submitBookedHourChange' };
    this.cdr.markForCheck();
  }

  cancelConfirmation(): void {
    this.confirmationAction = null;
    this.cdr.markForCheck();
  }

  confirmAction(): void {
    if (!this.confirmationAction) {
      return;
    }

    const action = this.confirmationAction;
    this.confirmationAction = null;

    switch (action.type) {
      case 'approveDayOff':
        this.requiredNotificationsService.approveDayOffRequest(action.group.id);
        break;

      case 'rejectDayOff':
        this.requiredNotificationsService.rejectDayOffRequest(action.group.id);
        break;

      case 'approveSupervisorBookedHour':
        this.requiredNotificationsService.approveBookedHour(action.row.id);
        break;

      case 'rejectSupervisorBookedHour':
        this.requiredNotificationsService.rejectBookedHour(action.row.id);
        break;

      case 'approveSelfBookedHour':
        this.requiredNotificationsService.approveSelfBookedHour(action.row.id);
        break;

      case 'rejectSelfBookedHour':
        this.requiredNotificationsService.rejectSelfBookedHour(action.row.id);
        break;

      case 'submitBookedHourChange':
        this.confirmBookedHourChange();
        break;

      case 'approveAll':
        this.requiredNotificationsService.approveAllPending().subscribe();
        break;
    }

    this.cdr.markForCheck();
  }

  getConfirmationTitle(): string {
    switch (this.confirmationAction?.type) {
      case 'approveDayOff':
      case 'approveSupervisorBookedHour':
      case 'approveSelfBookedHour':
        return 'Approve Notification';

      case 'rejectDayOff':
      case 'rejectSupervisorBookedHour':
      case 'rejectSelfBookedHour':
        return 'Reject Notification';

      case 'submitBookedHourChange':
        return 'Confirm Hour Change';

      case 'approveAll':
        return 'Approve All Notifications';

      default:
        return 'Confirm Action';
    }
  }

  getConfirmationMessage(): string {
    switch (this.confirmationAction?.type) {
      case 'approveDayOff':
        return 'Are you sure you want to approve this time off request?';

      case 'rejectDayOff':
        return 'Are you sure you want to reject this time off request?';

      case 'approveSupervisorBookedHour':
      case 'approveSelfBookedHour':
        return 'Are you sure you want to approve these booked hours?';

      case 'rejectSupervisorBookedHour':
      case 'rejectSelfBookedHour':
        return 'Are you sure you want to reject these booked hours?';

      case 'submitBookedHourChange':
        return 'Are you sure you want to save this changed hour value?';

      case 'approveAll':
        return 'Are you sure you want to approve all pending notifications?';

      default:
        return 'Are you sure you want to continue?';
    }
  }

  getConfirmationButtonText(): string {
    switch (this.confirmationAction?.type) {
      case 'approveDayOff':
      case 'approveSupervisorBookedHour':
      case 'approveSelfBookedHour':
        return 'Approve';

      case 'rejectDayOff':
      case 'rejectSupervisorBookedHour':
      case 'rejectSelfBookedHour':
        return 'Reject';

      case 'submitBookedHourChange':
        return 'Save Change';

      case 'approveAll':
        return 'Approve All';

      default:
        return 'Confirm';
    }
  }

  isConfirmationDanger(): boolean {
    return (
      this.confirmationAction?.type === 'rejectDayOff' ||
      this.confirmationAction?.type === 'rejectSupervisorBookedHour' ||
      this.confirmationAction?.type === 'rejectSelfBookedHour'
    );
  }

  trackById(_: number, item: { id: number }): number {
    return item.id;
  }

  private openBookedHourChange(row: BookedHourNotificationDto, isSelfApproval: boolean): void {
    this.bookedHourChangeModal = {
      row,
      isSelfApproval,
      newBookedHours: this.round2(Number(row.hours) || 0),
      bypassEmployeeApproval: false,
      errorMessage: null,
    };

    this.cdr.markForCheck();
  }

  private confirmBookedHourChange(): void {
    if (!this.bookedHourChangeModal) {
      return;
    }

    const modal = this.bookedHourChangeModal;
    const cleanHours = this.round2(Number(modal.newBookedHours));

    if (modal.isSelfApproval) {
      this.requiredNotificationsService.requestSelfBookedHourChange(modal.row.id, cleanHours);
      this.closeBookedHourChangeModal();
      return;
    }

    if (modal.bypassEmployeeApproval) {
      this.requiredNotificationsService.approveBookedHourWithChange(modal.row.id, cleanHours);
    } else {
      this.requiredNotificationsService.requestBookedHourChange(modal.row.id, cleanHours);
    }

    this.closeBookedHourChangeModal();
  }

  private setBookedHourChangeError(message: string): void {
    if (!this.bookedHourChangeModal) {
      return;
    }

    this.bookedHourChangeModal = {
      ...this.bookedHourChangeModal,
      errorMessage: message,
    };

    this.cdr.markForCheck();
  }

  private isHoursUrl(url: string): boolean {
    const cleanUrl = String(url ?? '').split('?')[0].split('#')[0];
    return cleanUrl === '/hours' || cleanUrl.startsWith('/hours/');
  }

  private isMatch(a: number, b: number): boolean {
    return Math.abs(this.round2(a) - this.round2(b)) <= 0.01;
  }

  private round2(value: number): number {
    if (!Number.isFinite(value)) {
      return 0;
    }

    return Math.round((value + Number.EPSILON) * 100) / 100;
  }
}